/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { styles } from '@utils';
import createOpenGraphTags from '@rsc/utils/createOpenGraphTags';
import Button from '@rsc/ui-components/button/Button';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';

import Layout from '@components/layout';
import GearTagLine from '@components/GearTagLine';
import { getSportdogPageTitle } from '@util/helpers';
import {
  getLocalizedPagePath,
  LocalizedLink,
} from '@rsc/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import { ValidationMessage } from '@rsc/ui-components/form/FormRow';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalizedYup } from '@rsc/ui-components/hooks';

const useValidationSchema = () => {
  const { tt } = useSportdogInternationalContext();
  const yup = useLocalizedYup();

  return useMemo(
    () =>
      yup.object().shape({
        searchQuery: yup
          .string()
          .trim()
          .requiredLocalized(tt(x => x.notFoundPage.searchValidationMessage))
          .label(tt(s => s.notFoundPage.searchFieldLabel)),
      }),
    [tt, yup]
  );
};

const browser = typeof window !== 'undefined' && window;

const SearchInput = styled.input`
  display: block;
  border: 1px solid ${styles.colors.gray[100]};
  color: ${styles.colors.gray[500]};
  height: 40px;
  border-radius: 5px;
  margin: 15px 0;
  width: 100%;
  max-width: 600px;
  padding: 0 10px;
`;

function NotFoundPage({ location, pageContext }) {
  const Url = location.href;
  const { tt, locale } = useSportdogInternationalContext();
  const { linkHreflangValues } = pageContext;
  const validationSchema = useValidationSchema();

  const homePageLink = (
    <LocalizedLink to="/" locale={locale}>
      {tt(s => s.notFoundPage.homePageInlineLink)}
    </LocalizedLink>
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (data, e) => {
    e.preventDefault();
    window.location.href = `${getLocalizedPagePath('/search', locale)}?q=${
      data.searchQuery
    }`;
  };

  return (
    browser && (
      <Layout
        headTagData={{
          title: getSportdogPageTitle(tt(s => s.notFoundPage.pageTitle)),
          meta: createOpenGraphTags(
            {
              title: getSportdogPageTitle(tt(s => s.notFoundPage.pageTitle)),
              url: getLocalizedPagePath('/404', locale),
            },
            'sportdog'
          ),
        }}
        linkHreflangValues={linkHreflangValues}
      >
        <PageWrapper>
          <h1>{tt(s => s.notFoundPage.header)}</h1>
          <p>
            {tt(s => s.notFoundPage.tryToReachSubtitle, {
              url: <strong>{Url}</strong>,
            })}
          </p>
          <p>{tt(s => s.notFoundPage.body, { homePageLink })}</p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <label htmlFor="q">
              {tt(s => s.notFoundPage.searchFieldLabel)}
              <SearchInput
                name="q"
                id="q"
                type="text"
                placeholder={tt(s => s.notFoundPage.searchPlaceholder)}
                {...register('searchQuery')}
              />
            </label>
            <ValidationMessage>{errors.searchQuery?.message}</ValidationMessage>
            <Button type="submit">
              {tt(s => s.notFoundPage.searchButton)}
            </Button>
          </form>
        </PageWrapper>
        <GearTagLine />
      </Layout>
    )
  );
}

NotFoundPage.propTypes = {
  location: PropTypes.shape({ href: PropTypes.string.isRequired }).isRequired,
  pageContext: PropTypes.shape({
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default NotFoundPage;
